import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ServiceInfo } from '../lib/types';

export interface PendingScale {
  target: number;
  from: number;
  sending: boolean;
  since: number;
}

export type PendingScaleMap = Record<string, PendingScale>;

interface Options {
  timeoutMs?: number;
  onConfirmed?: (svc: ServiceInfo) => void;
  onUnconfirmed?: (svc: ServiceInfo, target: number) => void;
}

const DEFAULT_TIMEOUT_MS = 90_000;
const SWEEP_MS = 5_000;

// O stream do flow só confirma a escala na próxima volta do agent (segundos,
// às vezes dezenas): até lá a tela assume o alvo pedido, e só solta quando
// réplicas E máquinas chegam nele — máquina é o container de pé, não se inventa.
export function usePendingScale(
  services: ServiceInfo[],
  send: (svc: ServiceInfo, replicas: number, from: number) => Promise<unknown>,
  { timeoutMs = DEFAULT_TIMEOUT_MS, onConfirmed, onUnconfirmed }: Options = {}
) {
  const [pending, setPending] = useState<PendingScaleMap>({});
  const callbacks = useRef({ onConfirmed, onUnconfirmed });
  callbacks.current = { onConfirmed, onUnconfirmed };
  const servicesRef = useRef(services);
  servicesRef.current = services;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const drop = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setPending(p => {
      const next = { ...p };
      ids.forEach(id => delete next[id]);
      return next;
    });
  }, []);

  useEffect(() => {
    const confirmed = services.filter(s => {
      const p = pending[s.id];
      return (
        p && !p.sending && s.replicas === p.target && s.machines === p.target
      );
    });
    if (confirmed.length === 0) return;
    drop(confirmed.map(s => s.id));
    confirmed.forEach(s => callbacks.current.onConfirmed?.(s));
  }, [services, pending, drop]);

  const hasPending = Object.keys(pending).length > 0;
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(() => {
      const now = Date.now();
      const expired = Object.entries(pendingRef.current).filter(
        ([, v]) => !v.sending && now - v.since >= timeoutMs
      );
      if (expired.length === 0) return;
      drop(expired.map(([sid]) => sid));
      for (const [sid, v] of expired) {
        const live = servicesRef.current.find(s => s.id === sid);
        if (live && live.replicas !== v.target) {
          callbacks.current.onUnconfirmed?.(live, v.target);
        }
      }
    }, SWEEP_MS);
    return () => clearInterval(id);
  }, [hasPending, timeoutMs, drop]);

  const scale = useCallback(
    (svc: ServiceInfo, replicas: number, from: number): Promise<unknown> => {
      setPending(p => ({
        ...p,
        [svc.id]: { target: replicas, from, sending: true, since: Date.now() },
      }));
      return send(svc, replicas, from).then(
        res => {
          setPending(p =>
            p[svc.id]
              ? { ...p, [svc.id]: { ...p[svc.id], sending: false, since: Date.now() } }
              : p
          );
          return res;
        },
        err => {
          drop([svc.id]);
          throw err;
        }
      );
    },
    [send, drop]
  );

  const effective = useMemo(
    () =>
      services.map(s =>
        pending[s.id] ? { ...s, replicas: pending[s.id].target } : s
      ),
    [services, pending]
  );

  return { services: effective, pending, scale };
}
